import { onValue, ref, update } from "firebase/database";
import { JOB_TIMEOUT } from "../i18n/keysTranslations";
import { getPathDatabaseByRole, renewToken } from "./auth";
import axios from "../lib/axios";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import { db } from "../lib/firebase";
import i18next from "i18next";
import { getLanguageFormatted } from "../utils/string";
import {
  CREATIVE_JOB_IN_PROGRESS_STATUSES,
  CREATIVE_JOB_STATUS,
} from "../utils/creativeJobStatus";

const MINUTE_IN_MS = 60 * 1000;
const CREATIVE_JOB_TIMEOUT_MS = 20 * MINUTE_IN_MS;
const CREATIVE_JOB_TIMEOUTS_BY_TYPE = {
  generateImages: 9 * MINUTE_IN_MS,
  resizeImage: 9 * MINUTE_IN_MS,
  editImage: 9 * MINUTE_IN_MS,
  editImagesBatch: 18 * MINUTE_IN_MS,
  resizeImagesBatch: 20 * MINUTE_IN_MS,
  animateImage: 10 * MINUTE_IN_MS,
  generateVideo: 30 * MINUTE_IN_MS,
  generateRecommendedSettingsPlatform: 20 * MINUTE_IN_MS,
  generateCustomReport: 15 * MINUTE_IN_MS,
  analyzeImageLayers: 9 * MINUTE_IN_MS,
  analyzeCreativeTemplate: 9 * MINUTE_IN_MS,
  produceCreativeTemplate: 61 * MINUTE_IN_MS,
};

const getCreativeJobError = (error = {}) => ({
  ok: false,
  ...error,
});

const getCreativeJobTimeoutMs = ({ jobType, timeoutMs }) =>
  timeoutMs ??
  CREATIVE_JOB_TIMEOUTS_BY_TYPE[jobType] ??
  CREATIVE_JOB_TIMEOUT_MS;

const getCreativeJobTimeoutError = () => ({
  ok: false,
  message: "Creative job timeout",
  code: JOB_TIMEOUT,
});

/**
 * Calls createJob HTTPS function, then waits on RTDB jobs/{userID}/{jobID}
 * until status is success, error, or cancelled (keeps waiting on pending/processing).
 *
 * Resolves only with successful job responses. Rejects when createJob fails,
 * when the job finishes with an error response, or when the job times out.
 */
export const createCreativeJobAndWaitForResponse = async ({
  dispatch,
  jobType,
  payload,
  timeoutMs,
  language: languageOverride,
  onJobCreated = () => {},
}) => {
  const timeoutMsFormatted = getCreativeJobTimeoutMs({ jobType, timeoutMs });
  const token = await dispatch(renewToken());
  const { userID } = dispatch(getPathDatabaseByRole());

  const language =
    languageOverride ?? getLanguageFormatted(i18next.language);

  const { data } = await axios({
    method: "post",
    url: `${getCloudFunctionUrl("createJob")}/createJob`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      jobType,
      payload,
      language,
    },
  });

  const jobID = data?.data?.jobID;

  if (!data?.ok || !jobID || !userID) {
    throw getCreativeJobError(data);
  }

  onJobCreated({ jobID });

  return await new Promise((resolve, reject) => {
    const jobRef = ref(db, `jobs/${userID}/${jobID}`);
    let isSettled = false;
    let unsubscribe = null;
    let timeoutID = null;

    const onCleanup = () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
      if (timeoutID != null) {
        clearTimeout(timeoutID);
        timeoutID = null;
      }
    };

    const onResolve = (response) => {
      if (isSettled) return;
      const responseFormatted = getCreativeJobError(response);

      if (!responseFormatted?.ok) {
        onReject(responseFormatted);
        return;
      }

      isSettled = true;
      onCleanup();
      resolve(responseFormatted);
    };

    const onReject = (error) => {
      if (isSettled) return;
      isSettled = true;
      onCleanup();
      reject(error);
    };

    timeoutID = setTimeout(async () => {
      const timeoutError = getCreativeJobTimeoutError();

      try {
        await update(jobRef, {
          status: CREATIVE_JOB_STATUS.CANCELLED,
          response: timeoutError,
        });
      } catch (error) {
        console.error("Error cancelling creative job after timeout", error);
      }

      onReject(timeoutError);
    }, timeoutMsFormatted);

    unsubscribe = onValue(
      jobRef,
      (snapshot) => {
        const jobData = snapshot.val();

        if (!jobData) return;
        if (CREATIVE_JOB_IN_PROGRESS_STATUSES.has(jobData.status)) return;

        if (jobData.status === CREATIVE_JOB_STATUS.SUCCESS) {
          onResolve(jobData.response);
          return;
        }

        if (
          jobData.status === CREATIVE_JOB_STATUS.ERROR ||
          jobData.status === CREATIVE_JOB_STATUS.CANCELLED
        ) {
          onReject(getCreativeJobError(jobData.response));
          return;
        }
      },
      (error) => {
        onReject(error);
      }
    );
  });
};
